import { Controller } from "@hotwired/stimulus";
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";

// Só as peças de barra e linha (os tipos do `gkg_grafico`): o `registerables`
// inteiro quase dobrava o `application.js`.
Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
);

// Quantidade de `--gkg-ui-serie-N` em `_design-system.scss`; acima disso a sequência recomeça.
const CORES = 6;

const numero = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 });

// Fundo dos baldes de fim de semana: a sazonalidade semanal é o padrão mais visível das
// séries de uso, e sem a marcação cada vale parece uma queda.
const fimDeSemana = {
  id: "gkgFimDeSemana",
  beforeDatasetsDraw(grafico, _args, opcoes) {
    const indices = opcoes.indices || [];
    if (indices.length === 0) return;

    const { ctx, chartArea, scales } = grafico;
    const eixo = scales.x;
    const largura = eixo.getPixelForValue(1) - eixo.getPixelForValue(0);

    ctx.save();
    ctx.fillStyle = opcoes.cor;
    indices.forEach((indice) => {
      ctx.fillRect(eixo.getPixelForValue(indice) - largura / 2, chartArea.top, largura, chartArea.height);
    });
    ctx.restore();
  },
};

// Config vinda do `gkg_grafico`: { tipo: "bar"|"line", rotulos: [...],
//   series: [{ nome, valores: [1, null, 3], unidade, fantasma }], fins_de_semana: [5, 6] }.
// `null` é balde sem envio, e a linha quebra nele (`spanGaps: false`) em vez de emendar.
// A série `fantasma` é o período anterior: cinza e tracejada, atrás da atual, sem
// entrar na paleta.
export default class extends Controller {
  static targets = ["canvas"];
  static values = { config: Object };

  connect() {
    this.herdarTipografia();

    const paleta = this.paleta();
    const { tipo, rotulos, series, fins_de_semana: finsDeSemana } = this.configValue;
    let posicao = 0;

    this.grafico = new Chart(this.canvasTarget, {
      type: tipo,
      plugins: [fimDeSemana],
      data: {
        labels: rotulos,
        datasets: series.map((serie) => {
          if (serie.fantasma) return this.conjuntoFantasma(serie, tipo);

          return this.conjunto(serie, paleta[posicao++ % paleta.length], tipo);
        }),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        spanGaps: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          y: { beginAtZero: true, ticks: { callback: (valor) => numero.format(valor) } },
        },
        plugins: {
          // Com uma série só, o título do cartão já faz o papel da legenda.
          legend: { display: series.length > 1, position: "bottom" },
          tooltip: { callbacks: { label: (contexto) => this.rotuloDoPonto(contexto) } },
          gkgFimDeSemana: { indices: finsDeSemana || [], cor: this.corDoFimDeSemana() },
        },
      },
    });
  }

  // Sem destruir, a volta pelo histórico do Turbo desenharia sobre um canvas ainda preso
  // à instância anterior.
  disconnect() {
    this.grafico?.destroy();
    this.grafico = null;
  }

  conjunto(serie, cor, tipo) {
    return {
      label: serie.nome,
      data: serie.valores,
      unidade: serie.unidade,
      backgroundColor: cor,
      borderColor: cor,
      borderWidth: tipo === "line" ? 2 : 0,
      borderRadius: tipo === "bar" ? 2 : 0,
      // Um dia entre dois buracos é só um ponto, sem segmento: precisa ser visível.
      pointRadius: tipo === "line" ? 3 : 0,
      pointHoverRadius: tipo === "line" ? 5 : 0,
      tension: 0,
    };
  }

  conjuntoFantasma(serie, tipo) {
    const cor = getComputedStyle(document.documentElement).getPropertyValue("--gkg-ui-chart-7").trim();

    return {
      ...this.conjunto(serie, cor, tipo),
      backgroundColor: tipo === "bar" ? cor : "transparent",
      borderDash: tipo === "line" ? [4, 4] : [],
      borderWidth: tipo === "line" ? 1.5 : 0,
      pointRadius: 0,
      pointHoverRadius: 3,
      order: 1,
    };
  }

  corDoFimDeSemana() {
    const tinta = getComputedStyle(document.documentElement).getPropertyValue("--gkg-ui-ink").trim();

    return `color-mix(in srgb, ${tinta} 5%, transparent)`;
  }

  rotuloDoPonto(contexto) {
    const valor = contexto.parsed.y;
    const unidade = contexto.dataset.unidade;

    if (valor === null || valor === undefined) return `${contexto.dataset.label}: sem envio`;

    return `${contexto.dataset.label}: ${numero.format(valor)}${unidade ? ` ${unidade}` : ""}`;
  }

  herdarTipografia() {
    const estilo = getComputedStyle(document.documentElement);

    Chart.defaults.font.family = getComputedStyle(document.body).fontFamily;
    Chart.defaults.color = estilo.getPropertyValue("--gkg-ui-ink-2").trim();
  }

  // As cores vêm dos tokens CSS, para o gráfico seguir a mesma ordem de `.gkg-bars`.
  paleta() {
    const estilo = getComputedStyle(document.documentElement);

    return Array.from({ length: CORES }, (_, indice) => estilo.getPropertyValue(`--gkg-ui-serie-${indice + 1}`).trim());
  }
}
